import {
  createContext,
  useContext,
  ReactNode,
  FC,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useLocation } from "react-router-dom";

import config from "config";
import { redactCredentialParams } from "utils/redactCredentialParams";

type SignalData = {
  dl: string;
  dr: string;
};

/** Body for abra's `/ev` named-event endpoint. `ei` chains this event to
 * the session abra started on this device's first one. */
type SignalEventPayload = {
  en: string;
  ev: string;
  dl: string;
  ei?: string;
};

// abra answers `/ev` with `{ eid }` (src/index.ts, the /ev route); `a_esid`
// is the shape its /f route uses and what after-www's hook still reads.
type SignalEventResponse = {
  eid?: string;
  a_esid?: string;
};

type SignalContextType = {
  trackSignal: (data?: Partial<SignalData>) => void;
  /** Fire-and-forget named event — the signals half of AnalyticsContext's
   * PostHog + signals fan-out; nothing else should call this directly. */
  trackSignalEvent: (
    name: string,
    properties?: Record<string, unknown>,
  ) => void;
};

const SignalContext = createContext<SignalContextType | undefined>(undefined);

// Dev's config already points signalHost at a local abra/stub
// (localhost:8281), never at prod, so the only real reason to skip
// sending is a host that was never configured at all.
const sendSignal = async (data: SignalData) => {
  if (!config.signalHost) {
    console.debug("Skipping signal capture: no signalHost configured");
    return;
  }

  try {
    await fetch(`${config.signalHost}/e`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  } catch (error) {
    console.error("Error sending signal event:", error);
  }
};

const sendSignalEvent = async (
  payload: SignalEventPayload,
): Promise<SignalEventResponse | null> => {
  if (!config.signalHost) {
    console.debug("Skipping signal capture: no signalHost configured");
    return null;
  }

  try {
    const response = await fetch(`${config.signalHost}/ev`, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) return null;
    return (await response
      .json()
      .catch(() => null)) as SignalEventResponse | null;
  } catch (error) {
    // Signals are best-effort — failures never reach Sentry.
    console.debug("Error sending signal event:", error);
    return null;
  }
};

export const SignalProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // Both URL fields are scrubbed here, whatever route they came from: the
  // quote flow's credential (a link token or a signed lead payload) rides
  // the URL, and a reload or a same-tab redirect surfaces it as the
  // referrer too — not only as the location.
  const trackSignal = useCallback((data?: Partial<SignalData>) => {
    sendSignal({
      dl: redactCredentialParams(data?.dl || window.location.href),
      dr: redactCredentialParams(data?.dr || document.referrer),
    });
  }, []);

  // The event-session id abra assigns on this device's first `/ev` call
  // and expects back on every later one — held for the provider's
  // lifetime, i.e. the whole page.
  const eventSessionId = useRef<string | undefined>(undefined);

  const trackSignalEvent = useCallback(
    (name: string, properties?: Record<string, unknown>) => {
      sendSignalEvent({
        en: name,
        ev: JSON.stringify(properties ?? {}),
        dl: redactCredentialParams(window.location.href),
        ei: eventSessionId.current,
      }).then((response) => {
        const assigned = response?.eid ?? response?.a_esid;
        if (assigned) {
          eventSessionId.current = assigned;
        }
      });
    },
    [],
  );

  return (
    <SignalContext.Provider value={{ trackSignal, trackSignalEvent }}>
      {children}
    </SignalContext.Provider>
  );
};

// Separate component that uses useLocation and must be inside Router
export const SignalPageTracker: FC = () => {
  const location = useLocation();
  const previousPathname = useRef<string | null>(null);
  const externalReferrer = useRef(document.referrer);
  const { trackSignal } = useSignal();

  useEffect(() => {
    // Determine the referrer based on navigation type
    let referrer: string;

    if (previousPathname.current === null) {
      // First page load - use external referrer
      referrer = externalReferrer.current;
    } else if (previousPathname.current !== location.pathname) {
      // Internal navigation - use previous page as referrer
      referrer = `${window.location.origin}${previousPathname.current}`;
    } else {
      // Same page (e.g., hash change or query param change) - skip tracking
      return;
    }

    trackSignal({ dl: window.location.href, dr: referrer });

    // Update previous pathname for next navigation
    previousPathname.current = location.pathname;
  }, [location, trackSignal]);

  return null;
};

export const useSignal = () => {
  const context = useContext(SignalContext);
  if (context === undefined) {
    throw new Error("useSignal must be used within a SignalProvider");
  }
  return context;
};
